import React, { useEffect, useState, useMemo } from 'react'
import styled from '@emotion/styled'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { login } from '../../../reducer/authSlice'
import { showAlert } from '../../../reducer/alertSlice'
import { FaUser, FaLock } from 'react-icons/fa'
import { useLogin } from '../../../api/auth/hooks'
import PrimaryButton from '../../atoms/PrimaryButton'
import Typography from '../../atoms/Typography'

const Login = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  
  const loginMutation = useLogin()
  const [id, setId] = useState('')
  const [password, setPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated)
  // id나 password 중 하나라도 공백이면 disabled (+ 폼 전송중일때도도)
  const isDisabled = useMemo(() => !id.trim() || !password.trim() || isSubmitting, [id, password, isSubmitting])

  // 로그인을 이미 했다면 홈으로 이동시킴
  useEffect(() => {
    if (isAuthenticated === true) {
      navigate('/home')
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    loginMutation.mutate({
      id: id,
      password: password
    }, {
      onSuccess: (data) => {
        switch (data.code) {
          case 0:
            console.log("@Login - Success")
            // 로그인 정보 저장
            const { user, access_token } = data.data
            dispatch(login({ user, access_token }))
            navigate('/home')
            break
          default:
            dispatch(showAlert({
              msg: "로그인에 실패했습니다. 다시 시도해주세요."
            }))
        }
      },
      onError: (error) => {
        // 네트워크 오류인 경우
        if (!error.response) {
          return
        }

        switch (error.response.data.code) {
          case -5:
            dispatch(showAlert({
              msg: "존재하지 않는 이메일 혹은\n잘못된 비밀번호를 입력했습니다."
            }))
            break
          default:
            dispatch(showAlert({
              msg: "로그인에 실패했습니다. 다시 시도해주세요."
            }))
        }
      },
      onSettled: () => {
        setIsSubmitting(false)
      },
    })
  }

  return (
    <Container>
      <LoginForm onSubmit={handleSubmit}>
        <Title variant="h3" fontS>AIRS</Title>
        <InputWrapper>
          <StyledIcon>
            <FaUser />
          </StyledIcon>
          <StyledInput type="text" placeholder="앱노트 이메일" onChange={(e) => setId(e.target.value)} disabled={isSubmitting} />
        </InputWrapper>
        <InputWrapper>
          <StyledIcon>
            <FaLock />
          </StyledIcon>
          <StyledInput type="password" placeholder="비밀번호" onChange={(e) => setPassword(e.target.value)} value={password} disabled={isSubmitting} />
        </InputWrapper>
        <LoginButton type="submit" disabled={isDisabled}>
          {isSubmitting ? '로그인 진행 중…' : '로그인'}
        </LoginButton>
      </LoginForm>
      <FooterText>Appknot Internal Review System</FooterText>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  background-color: var(--base-light-color);
  align-items: center;
  justify-content: center;
  position: relative;
`;

const LoginForm = styled.form`
  width: 360px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  background-color: #fff;
  padding: 40px;
  border-radius: 16px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  // box-shadow: 0px 0px 48px 0px rgba(0, 0, 0, 0.08);
`;

const Title = styled(Typography)`
  font-weight: 800;
  color: var(--main-dark-color);
  text-align: center;
  margin: 0 0 10px;
  letter-spacing: 1px;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid var(--sub-dark-color);
  transition: border-bottom-color 0.2s, box-shadow 0.2s;

  &:focus-within {
    border-bottom-color: var(--main-color);
    box-shadow: 0 1px 0 0 var(--main-light-color);
  }
`;

const StyledIcon = styled.div`
  display: flex;
  align-items: center;
  margin-right: 16px;
  color: #777777;
  font-size: 18px;
`;
const StyledInput = styled.input`
  width: 100%;
  font-family: 'Pretendard';
  font-weight: 400;
  font-size: 16px;
  color: var(--dark-color);
  line-height: 100%;
  letter-spacing: -0.01em;
  border: none;
  outline: none;
  background: transparent;
`;

const LoginButton = styled(PrimaryButton)`
    margin-top: 20px;
    border-radius: 8px;
`;

const FooterText = styled.div`
  font-family: 'Gotham';
  font-weight: 300;
  position: absolute;
  bottom: 20px;
  width: 100%;
  text-align: center;
  font-size: 12px;
  color: var(--lightgray-color, #777);
  letter-spacing: 0.05em;
`;

export default Login;